<template>
  <div class="bc-overlay" @click.self="$emit('close')">
    <div class="bc-panel" role="dialog" aria-modal="true">
      <button class="bc-close" @click="$emit('close')" aria-label="Close">&times;</button>

      <div v-if="loading" class="bc-loading">Loading add-ons…</div>

      <div v-else-if="loadError" class="bc-alert">{{ loadError }}</div>

      <div v-else-if="!addon" class="bc-alert">No add-on is available for this meter.</div>

      <template v-else>
        <div class="bc-h">Add capacity</div>
        <div class="bc-addon">{{ addon.label }}</div>
        <div class="bc-mode">
          {{ addon.mode === 'recurring' ? 'Recurring — renews monthly' : 'One-off — non-expiring balance' }}
        </div>

        <div v-if="inTrial" class="bc-trial">
          Add-ons are available after your trial starts billing.
        </div>

        <template v-else>
          <div class="bc-qty">
            <label>Quantity</label>
            <div class="bc-qty__ctl">
              <button type="button" @click="dec" :disabled="quantity <= 1">−</button>
              <input type="number" min="1" v-model.number="quantity" />
              <button type="button" @click="quantity++">+</button>
            </div>
          </div>

          <div class="bc-price">
            <span>{{ addon.mode === 'recurring' ? 'Per month' : 'Total' }}</span>
            <span class="bc-price__v">{{ money(totalPence) }}<em v-if="addon.mode === 'recurring'">/mo</em></span>
          </div>
          <div v-if="vatNote" class="bc-vat">{{ vatNote }}</div>
          <div v-if="netNote" class="bc-vat bc-vat--net">{{ netNote }}</div>
          <div v-if="addon.mode === 'one_off' && addon.grant_per_unit" class="bc-grant">
            Adds {{ (addon.grant_per_unit * quantity).toLocaleString() }} {{ addon.unit }} to your balance.
          </div>
          <div v-if="addon.mode === 'recurring'" class="bc-grant">
            Active from now; charged again each month until you cancel.
          </div>

          <div v-if="purchaseError" class="bc-alert bc-alert--err">{{ purchaseError }}</div>

          <button class="bc-buy" :disabled="busy || quantity < 1" @click="buy">
            {{ busy ? 'Processing…' : `Buy · ${money(totalPence)}` }}
          </button>
          <div class="bc-fine">
            {{ billingMode === 'dev'
              ? 'Test mode — no card is charged.'
              : 'Charged to the card on file for this workspace.' }}
          </div>
        </template>
      </template>
    </div>
  </div>
</template>

<script>
/**
 * BuyCapacity — the self-serve "Add capacity" purchase panel (add-ons slice).
 *
 * Opened from the usage rail's per-meter add-on link. Resolves the add-on SKU
 * for the given meter from GET /api/v1/me/addons/catalogue, shows the price
 * HONESTLY (server-quoted, in pence → £), and purchases via
 * POST /api/v1/me/addons/purchase.
 *
 * Outcomes:
 *   - one-off credited → emits 'purchased' (parent reloads /me/usage).
 *   - recurring active  → emits 'purchased' (heartbeat already reflected it).
 *   - hosted checkout   → redirects to the returned checkout_url.
 *   - trial-blocked      → the panel shows the honest "after your trial" copy.
 */
import useApi from "@apexl/icarus-core/src/services/api";

export default {
  name: "BuyCapacity",
  props: {
    meter: { type: String, required: true },
  },
  emits: ["close", "purchased"],
  data() {
    return {
      api: useApi(),
      loading: true,
      loadError: "",
      catalogue: {},
      currency: "GBP",
      billingMode: "dev",
      inTrial: false,
      quantity: 1,
      busy: false,
      purchaseError: "",
    };
  },
  computed: {
    addonKey() {
      const entry = Object.entries(this.catalogue).find(([, v]) => v.meter === this.meter);
      return entry ? entry[0] : null;
    },
    addon() {
      return this.addonKey ? this.catalogue[this.addonKey] : null;
    },
    // What the card is charged: charge_pence is VAT included (Hermes); the
    // server falls back to price_pence when Hermes sends no VAT figures.
    totalPence() {
      if (!this.addon) return 0;
      const unit = this.addon.charge_pence ?? this.addon.price_pence;
      return unit * Math.max(1, this.quantity);
    },
    // VAT wording comes only from Hermes's figures (vat_rate / vat_pence).
    vatRate() {
      const r = Number(this.addon?.vat_rate);
      return Number.isFinite(r) && r > 0 && Number(this.addon?.vat_pence) > 0 ? r : null;
    },
    vatNote() {
      return this.vatRate !== null ? `Includes VAT at ${this.formatRate(this.vatRate)}%` : "";
    },
    netNote() {
      if (this.vatRate === null || !Number.isFinite(Number(this.addon?.net_pence))) return "";
      const net = Number(this.addon.net_pence) * Math.max(1, this.quantity);
      return `${this.money(net)}${this.addon.mode === 'recurring' ? '/mo' : ''} before VAT`;
    },
  },
  mounted() {
    this.loadCatalogue();
  },
  methods: {
    async loadCatalogue() {
      try {
        const res = await this.api.get("/api/v1/me/addons/catalogue");
        const body = res?.data?.data ?? res?.data ?? res;
        this.catalogue = body?.catalogue ?? {};
        this.currency = body?.currency ?? "GBP";
        this.billingMode = body?.billing_mode ?? "dev";
        this.inTrial = !!body?.in_trial;
      } catch (e) {
        this.loadError = e?.response?.data?.error || e?.message || "Could not load add-ons.";
      } finally {
        this.loading = false;
      }
    },
    dec() {
      if (this.quantity > 1) this.quantity--;
    },
    formatRate(r) {
      return Number.isInteger(r) ? String(r) : String(Number(r.toFixed(2)));
    },
    money(pence) {
      const symbol = this.currency === "GBP" ? "£" : "";
      return symbol + (pence / 100).toFixed(2).replace(/\.00$/, "");
    },
    async buy() {
      if (!this.addonKey) return;
      this.busy = true;
      this.purchaseError = "";
      try {
        const res = await this.api.post("/api/v1/me/addons/purchase", {
          addon_key: this.addonKey,
          quantity: Math.max(1, this.quantity),
          success_url: window.location.href,
          cancel_url: window.location.href,
        });
        const body = res?.data?.data ?? res?.data ?? res;
        if (body?.status === "checkout" && body?.checkout_url) {
          window.location.href = body.checkout_url;
          return;
        }
        this.$emit("purchased", body);
      } catch (e) {
        if (e?.response?.status === 403) {
          this.inTrial = true;
          this.purchaseError = e?.response?.data?.message || "Add-ons are available after your trial starts billing.";
        } else {
          this.purchaseError = e?.response?.data?.message || e?.response?.data?.error || e?.message || "Purchase failed.";
        }
      } finally {
        this.busy = false;
      }
    },
  },
};
</script>

<style scoped>
.bc-overlay {
  position: fixed; inset: 0; z-index: 1200;
  background: rgba(17, 20, 34, 0.42);
  display: flex; align-items: center; justify-content: center;
  padding: 20px;
}
.bc-panel {
  position: relative;
  width: 100%; max-width: 360px;
  background: var(--m-bg-card, #fff);
  border: 1px solid var(--m-border, #E4E7F5);
  border-radius: 14px;
  box-shadow: var(--m-shadow-card, 0 12px 40px rgba(20, 24, 46, 0.18));
  padding: 22px;
  color: var(--m-text-primary, #1B1E2B);
}
.bc-close {
  position: absolute; top: 10px; right: 12px;
  border: none; background: none; font-size: 1.4rem; line-height: 1;
  color: var(--m-text-tertiary, #9AA0AC); cursor: pointer;
}
.bc-loading, .bc-alert { font-size: 0.84rem; color: var(--m-text-secondary, #5A6072); padding: 8px 0; }
.bc-alert--err { color: var(--m-status-danger, #DC2626); margin: 10px 0 0; }
.bc-h { font-size: 0.66rem; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; color: var(--m-text-tertiary, #9AA0AC); }
.bc-addon { font-size: 1.15rem; font-weight: 750; letter-spacing: -0.01em; margin-top: 3px; }
.bc-mode { font-size: 0.76rem; color: var(--m-text-tertiary, #9AA0AC); margin-top: 2px; }
.bc-trial {
  margin-top: 14px; padding: 10px 12px; border-radius: 9px;
  background: var(--m-accent-soft, #EEF0FF); color: var(--m-accent, #4F46E5);
  font-size: 0.82rem; font-weight: 600;
}
.bc-qty { display: flex; align-items: center; justify-content: space-between; margin-top: 18px; }
.bc-qty label { font-size: 0.78rem; font-weight: 650; color: var(--m-text-secondary, #5A6072); }
.bc-qty__ctl { display: flex; align-items: center; gap: 6px; }
.bc-qty__ctl button {
  width: 30px; height: 30px; border-radius: 8px; border: 1px solid var(--m-border, #E4E7F5);
  background: var(--m-bg-subtle, #F7F8FC); font-size: 1.1rem; line-height: 1; cursor: pointer;
}
.bc-qty__ctl button:disabled { opacity: 0.4; cursor: default; }
.bc-qty__ctl input {
  width: 54px; text-align: center; height: 30px; border-radius: 8px;
  border: 1px solid var(--m-border, #E4E7F5); font: inherit; font-weight: 650;
}
.bc-price { display: flex; align-items: baseline; justify-content: space-between; margin-top: 16px; }
.bc-price > span:first-child { font-size: 0.78rem; color: var(--m-text-secondary, #5A6072); }
.bc-price__v { font-size: 1.35rem; font-weight: 750; font-family: var(--m-font-mono, monospace); }
.bc-price__v em { font-size: 0.72rem; font-style: normal; color: var(--m-text-tertiary, #9AA0AC); }
.bc-vat { font-size: 0.74rem; color: var(--m-text-secondary, #5A6072); margin-top: 4px; text-align: right; }
.bc-vat--net { color: var(--m-text-tertiary, #9AA0AC); margin-top: 1px; }
.bc-grant { font-size: 0.76rem; color: var(--m-text-tertiary, #9AA0AC); margin-top: 6px; }
.bc-buy {
  width: 100%; margin-top: 18px; padding: 11px; border-radius: 9px; border: none;
  background: var(--m-accent, #4F46E5); color: #fff; font: inherit; font-weight: 700;
  font-size: 0.88rem; cursor: pointer;
}
.bc-buy:disabled { opacity: 0.55; cursor: default; }
.bc-fine { font-size: 0.72rem; color: var(--m-text-tertiary, #9AA0AC); margin-top: 8px; text-align: center; }
</style>
